import React from 'react';
import { PartnerGrid, RoadmapTimeline, TokenInfo } from '../components/ecosystem';
import '../themes/ecosystem.css';

// ============================================
// Type Definitions
// ============================================
interface Partner {
    name: string;
    logo: string;
    type: string;
    link?: string;
}

interface Milestone {
    quarter: string;
    title: string;
    description: string;
    status: 'completed' | 'active' | 'upcoming';
}

interface TokenStat {
    value: string;
    label: string;
}

interface TeamMember {
    name: string;
    role: string;
    avatar: string;
    twitter?: string;
}

export interface EcosystemTemplateData {
    identity: {
        name: string;
        logo: string;
        tagline: string;
    };
    hero: {
        badge?: string;
        headline: string;
        gradientWord?: string;
        subline: string;
        primaryCta: { text: string; link: string };
        secondaryCta?: { text: string; link: string };
    };
    tokenStats?: TokenStat[];
    partners: Partner[];
    roadmap: Milestone[];
    team?: TeamMember[];
    cta?: {
        title: string;
        description: string;
        buttons: { text: string; link: string; primary?: boolean }[];
    };
    social: {
        twitter?: string;
        discord?: string;
        github?: string;
        telegram?: string;
    };
    links?: { text: string; href: string }[];
}

// ============================================
// Main Template Component
// ============================================
export const EcosystemTemplate: React.FC<{ data: EcosystemTemplateData }> = ({ data }) => {
    const { identity, hero, tokenStats, partners, roadmap, team, cta, social, links } = data;

    // Parse headline with gradient word
    const renderHeadline = () => {
        if (!hero.gradientWord) {
            return hero.headline;
        }
        const parts = hero.headline.split(hero.gradientWord);
        return (
            <>
                {parts[0]}
                <span className="gradient">{hero.gradientWord}</span>
                {parts.slice(1).join(hero.gradientWord)}
            </>
        );
    };

    return (
        <div className="ecosystem-layout" data-theme="ecosystem">
            {/* Header */}
            <header className="ecosystem-header">
                <div className="ecosystem-header-logo">
                    <img src={identity.logo} alt={identity.name} />
                    <span>{identity.name}</span>
                </div>

                <nav className="ecosystem-header-nav">
                    <a href="#partners">Partners</a>
                    <a href="#roadmap">Roadmap</a>
                    {team && <a href="#team">Team</a>}
                </nav>

                <a
                    href={hero.primaryCta.link}
                    className="ecosystem-header-cta"
                >
                    {hero.primaryCta.text}
                </a>
            </header>

            {/* Hero Section */}
            <section className="ecosystem-hero">
                {hero.badge && (
                    <div className="ecosystem-hero-badge">{hero.badge}</div>
                )}
                <h1 className="ecosystem-hero-headline">{renderHeadline()}</h1>
                <p className="ecosystem-hero-subline">{hero.subline}</p>
                <div className="ecosystem-hero-actions">
                    <a href={hero.primaryCta.link} className="ecosystem-hero-btn primary">
                        {hero.primaryCta.text}
                    </a>
                    {hero.secondaryCta && (
                        <a href={hero.secondaryCta.link} className="ecosystem-hero-btn secondary">
                            {hero.secondaryCta.text}
                        </a>
                    )}
                </div>
            </section>

            {/* Token Stats */}
            {tokenStats && tokenStats.length > 0 && (
                <section className="ecosystem-section">
                    <TokenInfo stats={tokenStats} />
                </section>
            )}

            {/* Partners */}
            {partners.length > 0 && (
                <section id="partners" className="ecosystem-section">
                    <h2 className="ecosystem-section-title">Backed By The Best</h2>
                    <p className="ecosystem-section-subtitle">
                        Investors, partners, and integrations powering our ecosystem
                    </p>
                    <PartnerGrid partners={partners} />
                </section>
            )}

            {/* Roadmap */}
            {roadmap.length > 0 && (
                <section id="roadmap" className="ecosystem-section">
                    <h2 className="ecosystem-section-title">Roadmap</h2>
                    <p className="ecosystem-section-subtitle">
                        Where we've been and where we're going
                    </p>
                    <RoadmapTimeline milestones={roadmap} />
                </section>
            )}

            {/* Team */}
            {team && team.length > 0 && (
                <section id="team" className="ecosystem-section">
                    <h2 className="ecosystem-section-title">The Team</h2>
                    <div className="ecosystem-team">
                        {team.map((member, index) => (
                            <div key={index} className="ecosystem-team-member">
                                <img
                                    src={member.avatar}
                                    alt={member.name}
                                    className="ecosystem-team-avatar"
                                />
                                <div className="ecosystem-team-name">{member.name}</div>
                                <div className="ecosystem-team-role">{member.role}</div>
                                {member.twitter && (
                                    <div className="ecosystem-team-social">
                                        <a href={member.twitter} target="_blank" rel="noopener noreferrer">
                                            @{member.twitter.split('/').pop()}
                                        </a>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* CTA */}
            {cta && (
                <section className="ecosystem-section">
                    <div className="ecosystem-cta">
                        <h3 className="ecosystem-cta-title">{cta.title}</h3>
                        <p className="ecosystem-cta-desc">{cta.description}</p>
                        <div className="ecosystem-cta-buttons">
                            {cta.buttons.map((btn, index) => (
                                <a
                                    key={index}
                                    href={btn.link}
                                    className={`ecosystem-hero-btn ${btn.primary ? 'primary' : 'secondary'}`}
                                >
                                    {btn.text}
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="ecosystem-footer">
                {links && links.length > 0 && (
                    <div className="ecosystem-footer-links">
                        {links.map((link, index) => (
                            <a key={index} href={link.href} target="_blank" rel="noopener noreferrer">
                                {link.text}
                            </a>
                        ))}
                        {social.twitter && <a href={social.twitter} target="_blank" rel="noopener noreferrer">Twitter</a>}
                        {social.discord && <a href={social.discord} target="_blank" rel="noopener noreferrer">Discord</a>}
                        {social.github && <a href={social.github} target="_blank" rel="noopener noreferrer">GitHub</a>}
                    </div>
                )}
                <p className="ecosystem-footer-text">
                    © {new Date().getFullYear()} {identity.name} • Built with Crypto Factory
                </p>
            </footer>
        </div>
    );
};

export default EcosystemTemplate;
